<template>
  <Dialog
    :is-open="modelValue"
    title="Filter Tasks"
    width="450px"
    @close="$emit('update:modelValue', false)"
  >
    <div :class="$style.filterSection">
      <h3>Time Period</h3>
      <div :class="$style.filterModeToggle">
        <label :class="$style.toggleLabel">
          <input type="checkbox" v-model="filterBySessionsCheckbox" />
          <span :class="$style.toggleText">Filter by start of task</span>
        </label>
      </div>
      <div :class="$style.periodFilters">
        <ToggleGroupRoot v-model="selectedPeriod" type="single">
          <ToggleGroupItem :value="TimePeriod.ALL" :class="$style.toggleItem">
            All
          </ToggleGroupItem>
          <ToggleGroupItem :value="TimePeriod.TODAY" :class="$style.toggleItem">
            Today
          </ToggleGroupItem>
          <ToggleGroupItem :value="TimePeriod.WEEK" :class="$style.toggleItem">
            This week
          </ToggleGroupItem>
          <ToggleGroupItem :value="TimePeriod.MONTH" :class="$style.toggleItem">
            This month
          </ToggleGroupItem>
        </ToggleGroupRoot>
      </div>

      <div v-if="showCustomRange" :class="$style.dateRange">
        <div :class="$style.dateInputs">
          <div :class="$style.dateInput">
            <label>From:</label>
            <input
              type="date"
              :value="localDateRange.start"
              @input="updateStartDate"
            />
          </div>
          <div :class="$style.dateInput">
            <label>To:</label>
            <input
              type="date"
              :value="localDateRange.end"
              @input="updateEndDate"
            />
          </div>
        </div>
      </div>

      <button
        :class="$style.toggleButton"
        @click="toggleCustomRange"
        type="button"
      >
        {{ showCustomRange ? "Use presets" : "Select range" }}
      </button>
    </div>
    <div :class="$style.filterSection">
      <h3>Completion Status</h3>
      <div :class="$style.completionFilters">
        <ToggleGroupRoot v-model="selectedCompletionStatus" type="single">
          <ToggleGroupItem
            :value="CompletionStatus.ALL"
            :class="$style.toggleItem"
          >
            All
          </ToggleGroupItem>
          <ToggleGroupItem
            :value="CompletionStatus.COMPLETED"
            :class="$style.toggleItem"
          >
            Completed
          </ToggleGroupItem>
          <ToggleGroupItem
            :value="CompletionStatus.NOT_COMPLETED"
            :class="$style.toggleItem"
          >
            Not Completed
          </ToggleGroupItem>
        </ToggleGroupRoot>
      </div>
    </div>
    <div :class="$style.filterSection">
      <h3>Topics</h3>
      <div :class="$style.topicFilters">
        <Chip
          :label="'Select All'"
          :selected="isAllTopicsSelected"
          @click="toggleAllTopics"
        />
        <Chip
          v-for="topic in availableTopics"
          :key="topic.id"
          :label="topic.name"
          :selected="selectedTopics.includes(topic.id)"
          :color="getTopicColor(topic.name)"
          @click="toggleTopicFilter(topic.id)"
        />
      </div>
    </div>
    <div :class="$style.filterSection">
      <h3>Projects</h3>
      <div :class="$style.projectFilterContainer">
        <ProjectMultiSelect
          :model-value="selectedProjects"
          :use-portal="false"
          @update:model-value="handleProjectSelection"
        />
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { topicService } from "../services/topicService";
import Dialog from "./Dialog.vue";
import ProjectMultiSelect from "./ProjectMultiSelect.vue";
import { ToggleGroupItem, ToggleGroupRoot } from "reka-ui";
import { TimePeriod, CompletionStatus } from "../types/enums";
import Chip from "./Chip.vue";

const props = defineProps<{
  modelValue: boolean;
  selectedTopics: string[];
  selectedProjects?: string[];
  selectedPeriod?: string;
  dateRange?: {
    start: string;
    end: string;
  };
  selectedCompletionStatus?: CompletionStatus;
  filterBySessions?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "update:selectedTopics", value: string[]): void;
  (e: "update:selectedProjects", value: string[]): void;
  (e: "update:selectedPeriod", value: string): void;
  (e: "update:dateRange", value: { start: string; end: string }): void;
  (e: "update:selectedCompletionStatus", value: CompletionStatus): void;
  (e: "update:filterBySessions", value: boolean): void;
}>();

const availableTopics = computed(() => topicService.getTopics());

const localDateRange = ref({
  start: props.dateRange?.start || "",
  end: props.dateRange?.end || "",
});

const selectedCompletionStatus = ref(
  props.selectedCompletionStatus || CompletionStatus.ALL
);
const selectedPeriod = ref(props.selectedPeriod || TimePeriod.ALL);

const filterBySessions = ref(props.filterBySessions !== false); // Default to true (any session)
const showCustomRange = ref(false);

const selectedProjects = ref<string[]>(props.selectedProjects || []);

// Computed property for checkbox binding (inverted logic)
const filterBySessionsCheckbox = computed({
  get: () => !filterBySessions.value,
  set: (value: boolean) => {
    filterBySessions.value = !value;
    emit("update:filterBySessions", filterBySessions.value);
  },
});

watch(
  () => props.dateRange,
  (newValue) => {
    if (newValue) {
      localDateRange.value = { ...newValue };
    }
  }
);

watch(
  () => props.selectedCompletionStatus,
  (newValue) => {
    if (newValue !== undefined) {
      selectedCompletionStatus.value = newValue;
    }
  }
);

watch(
  () => props.selectedPeriod,
  (newValue) => {
    selectedPeriod.value = newValue || TimePeriod.ALL;
  }
);

watch(
  () => props.filterBySessions,
  (newValue) => {
    if (newValue !== undefined) {
      filterBySessions.value = newValue;
    }
  }
);

watch(
  () => props.selectedProjects,
  (newValue) => {
    if (newValue !== undefined) {
      selectedProjects.value = [...newValue];
    }
  }
);

// Watch for changes to selectedPeriod and show custom range if CUSTOM is selected
watch(selectedPeriod, (newValue) => {
  if (newValue === TimePeriod.CUSTOM) {
    showCustomRange.value = true;
  } else if (showCustomRange.value) {
    showCustomRange.value = false;
  }
});

// Watch for changes to selectedCompletionStatus and emit them
watch(selectedCompletionStatus, (newValue) => {
  emit("update:selectedCompletionStatus", newValue);
});

// Watch for changes to selectedPeriod and emit them
watch(selectedPeriod, (newValue) => {
  emit("update:selectedPeriod", newValue);
});

const isAllTopicsSelected = computed(() => {
  return (
    availableTopics.value.length > 0 &&
    availableTopics.value.every((topic) =>
      props.selectedTopics.includes(topic.id)
    )
  );
});

function toggleAllTopics() {
  if (isAllTopicsSelected.value) {
    emit("update:selectedTopics", []);
  } else {
    emit(
      "update:selectedTopics",
      availableTopics.value.map((topic) => topic.id)
    );
  }
}

function toggleTopicFilter(topicId: string) {
  const newSelectedTopics = [...props.selectedTopics];
  const index = newSelectedTopics.indexOf(topicId);
  if (index === -1) {
    newSelectedTopics.push(topicId);
  } else {
    newSelectedTopics.splice(index, 1);
  }
  emit("update:selectedTopics", newSelectedTopics);
}

function updateStartDate(event: Event) {
  const input = event.target as HTMLInputElement;
  localDateRange.value.start = input.value;
  if (localDateRange.value.start && localDateRange.value.end) {
    selectedPeriod.value = TimePeriod.CUSTOM;
  }
  emit("update:dateRange", { ...localDateRange.value });
}

function updateEndDate(event: Event) {
  const input = event.target as HTMLInputElement;
  localDateRange.value.end = input.value;
  if (localDateRange.value.start && localDateRange.value.end) {
    selectedPeriod.value = TimePeriod.CUSTOM;
  }
  emit("update:dateRange", { ...localDateRange.value });
}

function toggleCustomRange() {
  showCustomRange.value = !showCustomRange.value;
  if (showCustomRange.value) {
    selectedPeriod.value = TimePeriod.CUSTOM;
  }
}

function handleProjectSelection(value: string[]) {
  selectedProjects.value = value;
  emit("update:selectedProjects", [...value]);
}

function getTopicColor(topicName: string): string | undefined {
  const colorMap: Record<string, string> = {
    Coding: "var(--primary-color)",
    Meeting: "var(--task-design)",
    Planning: "var(--task-meeting)",
    Research: "var(--task-research)",
    Documentation: "var(--task-break)",
    Other: "var(--task-other)",
    "In-Between": "var(--task-break)",
  };
  return colorMap[topicName];
}
</script>

<style module>
.filterSection {
  margin-bottom: 1rem;
}

.filterSection:last-child {
  margin-bottom: 0;
}

.filterSection h3 {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 0.5rem 0;
}

.filterModeToggle {
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.toggleLabel {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.toggleLabel input[type="checkbox"] {
  width: 1.25rem;
  height: 1.25rem;
  cursor: pointer;
}

.toggleText {
  font-weight: 500;
}

.periodFilters {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.toggleItem {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 8px 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}

.toggleItem:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.toggleItem[data-state="on"] {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
}

.toggleButton {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 0;
  align-self: flex-start;
  margin-top: 8px;
}

.toggleButton:hover {
  color: rgba(255, 255, 255, 0.8);
}

.dateRange {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.dateInputs {
  display: flex;
  gap: 1rem;
  width: 100%;
}

.dateInput {
  flex: 1;
  min-width: 0;
}

.dateInput label {
  display: block;
  margin-bottom: 0.25rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
}

.dateInput input[type="date"] {
  width: 100%;
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 16px;
  box-sizing: border-box;
}

.dateInput input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
}

.completionFilters {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.topicFilters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.projectFilterContainer {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (max-width: 480px) {
  .completionFilters {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
}
</style>
